/**
 * MDJ Ritual — Interactive Sliding Puzzle
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-effects.js
 *
 * Procedural canvas effects layered behind the puzzle board: rising steam
 * and a set of drifting botanical particles whose shape and palette are
 * chosen by product (via the particle_theme metafield read in the Liquid
 * section). Nothing here is a raster image or CDN asset — every shape is
 * drawn with canvas path primitives so the effect stays crisp at any
 * device pixel ratio and costs no network request.
 *
 * Public API consumed by mdj-core.js:
 *   start(mode)          - mode is 'idle' or 'reward'
 *   setReducedMotion(bool)
 *   destroy()
 */

(function () {
  'use strict';

  window.MDJRitual = window.MDJRitual || {};

  /**
   * Per-product particle themes. Each entry lists 2-3 particle "species"
   * mixed together, matching the botanical notes of that product.
   */
  var THEMES = {
    default: {
      steamColor: '245, 237, 216',
      species: [
        { type: 'dust', color: '200, 150, 42' },
        { type: 'leaf', color: '150, 163, 122' }
      ]
    },
    aeris: {
      // Nilgiri + peppermint + mullein + tulsi — cool, herbal greens
      steamColor: '232, 244, 238',
      species: [
        { type: 'leaf', color: '111, 174, 140' }, // peppermint
        { type: 'sprig', color: '124, 143, 90' }, // mullein
        { type: 'leaf', color: '163, 201, 168' } // tulsi
      ]
    },
    nocturne: {
      // Stars, lavender, moon dust — deep dusk tones
      steamColor: '40, 36, 58',
      species: [
        { type: 'star', color: '232, 223, 192' },
        { type: 'sprig', color: '138, 123, 181' }, // lavender
        { type: 'dust', color: '207, 214, 240' } // moon dust
      ]
    },
    roseva: {
      // Rose petals + gold dust
      steamColor: '245, 220, 220',
      species: [
        { type: 'petal', color: '217, 138, 156' },
        { type: 'dust', color: '200, 150, 42' }
      ]
    }
  };

  var IDLE_PARTICLE_COUNT = 14;
  var REWARD_PARTICLE_COUNT = 34;
  var IDLE_STEAM_COUNT = 3;
  var REWARD_STEAM_COUNT = 6;

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {Object} options
   * @param {string} options.theme
   * @param {boolean} options.enableSteam
   * @param {boolean} options.enableParticles
   * @param {boolean} options.reducedMotion
   * @param {number} options.speedMultiplier
   */
  function MDJEffects(canvas, options) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.options = options || {};
    this.reducedMotion = !!this.options.reducedMotion;
    this.speedMultiplier = this.options.speedMultiplier || 1;

    this.theme = THEMES[this.options.theme] || THEMES.default;

    this.mode = 'idle';
    this.width = 0;
    this.height = 0;
    this.dpr = window.devicePixelRatio || 1;

    this.particles = [];
    this.steamPuffs = [];

    this.rafId = null;
    this.lastFrameTime = 0;
    this.running = false;

    this._resizeObserver = null;
    this._onResize = this._onResize.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._tick = this._tick.bind(this);

    this._measure();

    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this.canvas);
    } else {
      window.addEventListener('resize', this._onResize);
    }

    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  /* ------------------------------------------------------------------ */
  /* Sizing                                                               */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype._measure = function () {
    var rect = this.canvas.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.dpr = window.devicePixelRatio || 1;

    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  };

  MDJEffects.prototype._onResize = function () {
    this._measure();
  };

  MDJEffects.prototype._onVisibilityChange = function () {
    if (document.hidden) {
      this._stopLoop();
    } else if (!this.reducedMotion) {
      this._startLoop();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Particle factory                                                     */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype._makeParticle = function (intensity) {
    var species = this.theme.species[Math.floor(Math.random() * this.theme.species.length)];
    var size = 6 + Math.random() * 10;

    return {
      type: species.type,
      color: species.color,
      x: Math.random() * this.width,
      y: this.height + size + Math.random() * 40,
      size: size,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.015,
      vx: (Math.random() - 0.5) * 0.15,
      vy: -(0.18 + Math.random() * 0.22) * (intensity === 'reward' ? 1.6 : 1),
      drift: Math.random() * Math.PI * 2,
      driftSpeed: 0.004 + Math.random() * 0.006,
      opacity: 0,
      targetOpacity: 0.35 + Math.random() * 0.35,
      life: 0,
      maxLife: 6000 + Math.random() * 5000
    };
  };

  MDJEffects.prototype._makeSteamPuff = function (intensity) {
    var baseSize = this.width * (0.28 + Math.random() * 0.18);
    return {
      x: this.width * (0.3 + Math.random() * 0.4),
      y: this.height * (0.85 + Math.random() * 0.1),
      size: baseSize,
      vy: -(0.12 + Math.random() * 0.1) * (intensity === 'reward' ? 1.5 : 1),
      drift: (Math.random() - 0.5) * 0.06,
      opacity: 0,
      targetOpacity: 0.08 + Math.random() * 0.06,
      life: 0,
      maxLife: 7000 + Math.random() * 4000
    };
  };

  /* ------------------------------------------------------------------ */
  /* Public control                                                       */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype.start = function (mode) {
    this.mode = mode === 'reward' ? 'reward' : 'idle';

    var particleTarget = this.mode === 'reward' ? REWARD_PARTICLE_COUNT : IDLE_PARTICLE_COUNT;
    var steamTarget = this.mode === 'reward' ? REWARD_STEAM_COUNT : IDLE_STEAM_COUNT;

    if (this.options.enableParticles) {
      while (this.particles.length < particleTarget) {
        this.particles.push(this._makeParticle(this.mode));
      }
    }

    if (this.options.enableSteam) {
      while (this.steamPuffs.length < steamTarget) {
        this.steamPuffs.push(this._makeSteamPuff(this.mode));
      }
    }

    if (this.reducedMotion) {
      this._renderStaticFrame();
    } else {
      this._startLoop();
    }
  };

  MDJEffects.prototype.setReducedMotion = function (value) {
    this.reducedMotion = !!value;
    if (this.reducedMotion) {
      this._stopLoop();
      this._renderStaticFrame();
    } else {
      this._startLoop();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Animation loop                                                       */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype._startLoop = function () {
    if (this.running || document.hidden) return;
    this.running = true;
    this.lastFrameTime = performance.now();
    this.rafId = requestAnimationFrame(this._tick);
  };

  MDJEffects.prototype._stopLoop = function () {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  MDJEffects.prototype._tick = function (now) {
    if (!this.running) return;

    var dt = Math.min(64, now - this.lastFrameTime) * this.speedMultiplier;
    this.lastFrameTime = now;

    this.ctx.clearRect(0, 0, this.width, this.height);

    if (this.options.enableSteam) this._updateAndDrawSteam(dt);
    if (this.options.enableParticles) this._updateAndDrawParticles(dt);

    this.rafId = requestAnimationFrame(this._tick);
  };

  MDJEffects.prototype._renderStaticFrame = function () {
    this.ctx.clearRect(0, 0, this.width, this.height);

    if (this.options.enableSteam) {
      for (var i = 0; i < this.steamPuffs.length; i++) {
        var puff = this.steamPuffs[i];
        puff.opacity = puff.targetOpacity;
        this._drawSteamPuff(puff);
      }
    }

    if (this.options.enableParticles) {
      for (var j = 0; j < this.particles.length; j++) {
        var particle = this.particles[j];
        particle.opacity = particle.targetOpacity;
        this._drawParticle(particle);
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Steam                                                                */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype._updateAndDrawSteam = function (dt) {
    for (var i = this.steamPuffs.length - 1; i >= 0; i--) {
      var puff = this.steamPuffs[i];
      puff.life += dt;
      puff.y += puff.vy * dt * 0.06;
      puff.x += puff.drift * dt * 0.06;

      var lifeFraction = puff.life / puff.maxLife;
      if (lifeFraction < 0.2) {
        puff.opacity = puff.targetOpacity * (lifeFraction / 0.2);
      } else if (lifeFraction > 0.75) {
        puff.opacity = puff.targetOpacity * (1 - (lifeFraction - 0.75) / 0.25);
      } else {
        puff.opacity = puff.targetOpacity;
      }

      if (puff.life >= puff.maxLife) {
        this.steamPuffs[i] = this._makeSteamPuff(this.mode);
        continue;
      }

      this._drawSteamPuff(puff);
    }
  };

  MDJEffects.prototype._drawSteamPuff = function (puff) {
    var ctx = this.ctx;
    var gradient = ctx.createRadialGradient(
      puff.x, puff.y, 0,
      puff.x, puff.y, puff.size
    );
    gradient.addColorStop(0, 'rgba(' + this.theme.steamColor + ', ' + puff.opacity + ')');
    gradient.addColorStop(1, 'rgba(' + this.theme.steamColor + ', 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(puff.x, puff.y, puff.size, 0, Math.PI * 2);
    ctx.fill();
  };

  /* ------------------------------------------------------------------ */
  /* Particles                                                            */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype._updateAndDrawParticles = function (dt) {
    for (var i = this.particles.length - 1; i >= 0; i--) {
      var particle = this.particles[i];
      particle.life += dt;
      particle.drift += particle.driftSpeed * dt;

      particle.x += (particle.vx + Math.sin(particle.drift) * 0.08) * dt * 0.06;
      particle.y += particle.vy * dt * 0.06;
      particle.rotation += particle.rotationSpeed * dt;

      var lifeFraction = particle.life / particle.maxLife;
      if (lifeFraction < 0.15) {
        particle.opacity = particle.targetOpacity * (lifeFraction / 0.15);
      } else if (lifeFraction > 0.8) {
        particle.opacity = particle.targetOpacity * (1 - (lifeFraction - 0.8) / 0.2);
      } else {
        particle.opacity = particle.targetOpacity;
      }

      var offscreen = particle.y < -particle.size - 20;
      if (particle.life >= particle.maxLife || offscreen) {
        this.particles[i] = this._makeParticle(this.mode);
        continue;
      }

      this._drawParticle(particle);
    }
  };

  MDJEffects.prototype._drawParticle = function (particle) {
    var ctx = this.ctx;
    ctx.save();
    ctx.translate(particle.x, particle.y);
    ctx.rotate(particle.rotation);
    ctx.globalAlpha = particle.opacity;

    switch (particle.type) {
      case 'leaf':
        this._drawLeaf(ctx, particle.size, particle.color);
        break;
      case 'petal':
        this._drawPetal(ctx, particle.size, particle.color);
        break;
      case 'star':
        this._drawStar(ctx, particle.size, particle.color);
        break;
      case 'sprig':
        this._drawSprig(ctx, particle.size, particle.color);
        break;
      case 'dust':
      default:
        this._drawDust(ctx, particle.size, particle.color);
        break;
    }

    ctx.restore();
  };

  /* -- Shape primitives, all procedural (no bitmap assets) -------------- */

  MDJEffects.prototype._drawLeaf = function (ctx, size, color) {
    var w = size * 0.55;
    var h = size;

    ctx.fillStyle = 'rgb(' + color + ')';
    ctx.beginPath();
    ctx.moveTo(0, -h / 2);
    ctx.bezierCurveTo(w, -h / 4, w, h / 4, 0, h / 2);
    ctx.bezierCurveTo(-w, h / 4, -w, -h / 4, 0, -h / 2);
    ctx.fill();

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = Math.max(0.5, size * 0.04);
    ctx.beginPath();
    ctx.moveTo(0, -h / 2);
    ctx.lineTo(0, h / 2);
    ctx.stroke();
  };

  MDJEffects.prototype._drawPetal = function (ctx, size, color) {
    var w = size * 0.7;
    var h = size * 1.1;

    ctx.fillStyle = 'rgb(' + color + ')';
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.quadraticCurveTo(w, 0, 0, -h / 2);
    ctx.quadraticCurveTo(-w, 0, 0, h / 2);
    ctx.fill();
  };

  MDJEffects.prototype._drawStar = function (ctx, size, color) {
    var spikes = 4;
    var outerRadius = size / 2;
    var innerRadius = outerRadius * 0.4;

    ctx.fillStyle = 'rgb(' + color + ')';
    ctx.beginPath();
    for (var i = 0; i < spikes * 2; i++) {
      var radius = i % 2 === 0 ? outerRadius : innerRadius;
      var angle = (Math.PI / spikes) * i;
      var px = Math.cos(angle) * radius;
      var py = Math.sin(angle) * radius;
      if (i === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.closePath();
    ctx.fill();
  };

  MDJEffects.prototype._drawSprig = function (ctx, size, color) {
    var length = size;
    var buds = 4;

    ctx.strokeStyle = 'rgb(' + color + ')';
    ctx.lineWidth = Math.max(0.6, size * 0.06);
    ctx.beginPath();
    ctx.moveTo(0, length / 2);
    ctx.lineTo(0, -length / 2);
    ctx.stroke();

    ctx.fillStyle = 'rgb(' + color + ')';
    for (var i = 0; i < buds; i++) {
      var t = i / (buds - 1);
      var y = length / 2 - t * length;
      var budSize = size * 0.16 * (1 - t * 0.3);

      ctx.beginPath();
      ctx.ellipse(-budSize * 0.9, y, budSize, budSize * 0.7, 0.4, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.ellipse(budSize * 0.9, y, budSize, budSize * 0.7, -0.4, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  MDJEffects.prototype._drawDust = function (ctx, size, color) {
    var radius = size * 0.22;

    var gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 3);
    gradient.addColorStop(0, 'rgba(' + color + ', 0.9)');
    gradient.addColorStop(1, 'rgba(' + color + ', 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgb(' + color + ')';
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fill();
  };

  /* ------------------------------------------------------------------ */
  /* Cleanup                                                              */
  /* ------------------------------------------------------------------ */

  MDJEffects.prototype.destroy = function () {
    this._stopLoop();

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    } else {
      window.removeEventListener('resize', this._onResize);
    }

    document.removeEventListener('visibilitychange', this._onVisibilityChange);

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.particles = [];
    this.steamPuffs = [];
  };

  window.MDJRitual.Effects = MDJEffects;
})();